import React, { useState, useEffect } from 'react';
import { overtimeAPI } from '../../services/api';
import { formatDate } from '../../utils/attendanceUtils';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Clock, 
  Plus, 
  Calendar, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  Edit
} from 'lucide-react';
import { format, parseISO } from 'date-fns';

const OvertimeManagement = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    date: format(new Date(), 'yyyy-MM-dd'),
    start_time: '',
    end_time: '',
    break_time: 0,
    remark: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  
  // Calculate default date range (21st of previous month to 20th of current month)
  const getDefaultDateRange = () => {
    const today = new Date();
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();
    
    // Start date: 21st of previous month
    const startDate = new Date(currentYear, currentMonth - 1, 21);

    // End date: 20th of next month
    const endDate = new Date(currentYear, currentMonth + 1, 20);
    
    return {
      start: format(startDate, 'yyyy-MM-dd'),
      end: format(endDate, 'yyyy-MM-dd')
    };
  };
  
  const [dateRange, setDateRange] = useState(getDefaultDateRange());

  useEffect(() => {
    fetchRequests();
  }, [dateRange]);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const response = await overtimeAPI.getMyOvertimeRequests();
      const allRequests = response.data.overtime_requests;
      
      // Filter requests by date range
      const filteredRequests = allRequests.filter(request => {
        const requestDate = request.date;
        return requestDate >= dateRange.start && requestDate <= dateRange.end;
      });
      
      setRequests(filteredRequests);
    } catch (error) {
      setError('Failed to fetch overtime requests');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      // Handle overnight shifts - if end time is earlier than start time, it's next day
      const startTime = formData.start_time;
      const endTime = formData.end_time;
      const isOvernightShift = endTime <= startTime;
      
      // Calculate end date (next day if overnight)
      const startDate = new Date(formData.date);
      const endDate = isOvernightShift 
        ? new Date(startDate.getTime() + 24 * 60 * 60 * 1000) // Add 1 day
        : startDate;
      
      const requestData = {
        ...formData,
        start_time: `${formData.date} ${formData.start_time}:00`,
        end_time: `${endDate.toISOString().split('T')[0]} ${formData.end_time}:00`,
        break_time: parseInt(formData.break_time)
      };

      await overtimeAPI.requestOvertime(requestData);
      setShowForm(false);
      setFormData({
        date: format(new Date(), 'yyyy-MM-dd'),
        start_time: '',
        end_time: '',
        break_time: 0,
        remark: ''
      });
      await fetchRequests();
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to submit overtime request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircle className="h-3 w-3 mr-1" />
            Approved
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <XCircle className="h-3 w-3 mr-1" />
            Rejected
          </span>
        );
      case 'cancelled':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            <XCircle className="h-3 w-3 mr-1" />
            Cancelled
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <AlertCircle className="h-3 w-3 mr-1" />
            Pending
          </span>
        );
    }
  };

  const handleCancelRequest = async (overtimeId) => {
    if (!window.confirm('Are you sure you want to cancel this overtime request?')) {
      return;
    }

    try {
      await overtimeAPI.cancelOvertimeRequest(overtimeId);
      await fetchRequests(); // Refresh the list
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to cancel overtime request');
    }
  };

  const calculateDuration = (startTime, endTime, breakTime) => {
    const start = parseISO(startTime);
    const end = parseISO(endTime);
    const durationMs = end.getTime() - start.getTime();
    const durationHours = (durationMs / (1000 * 60 * 60)) - (breakTime / 60);
    return Math.max(0, durationHours);
  };

  const calculateStatistics = () => {
    const approvedRequests = requests.filter(req => req.status === 'approved');
    const pendingRequests = requests.filter(req => req.status === 'pending');
    
    const totalApprovedHours = approvedRequests.reduce((total, req) => {
      return total + calculateDuration(req.start_time, req.end_time, req.break_time);
    }, 0);
    
    const totalPendingHours = pendingRequests.reduce((total, req) => {
      return total + calculateDuration(req.start_time, req.end_time, req.break_time);
    }, 0);
    
    return {
      approvedHours: totalApprovedHours.toFixed(1),
      pendingHours: totalPendingHours.toFixed(1),
      approvedCount: approvedRequests.length,
      pendingCount: pendingRequests.length
    };
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Clock className="h-6 w-6 text-blue-600 mr-3" />
              <h1 className="text-2xl font-bold text-gray-900">Overtime Management</h1>
            </div>
            <button
              onClick={() => setShowForm(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Request
            </button>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 flex items-center">
          <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
          <span className="text-red-700 text-sm">{error}</span>
        </div>
      )}

      {/* New Request Form */}
      {showForm && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              New Overtime Request
            </h3>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="date" className="block text-sm font-medium text-gray-700">
                    Date
                  </label>
                  <input
                    type="date"
                    id="date"
                    name="date"
                    required
                    value={formData.date}
                    onChange={handleInputChange}
                    min={(() => {
                      if (user?.ot_restriction_exempt) return undefined;
                      const limit = user?.ot_past_days_limit;
                      if (limit == null) return undefined;
                      const d = new Date();
                      d.setDate(d.getDate() - limit);
                      return d.toISOString().slice(0, 10);
                    })()}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>
                
                <div>
                  <label htmlFor="break_time" className="block text-sm font-medium text-gray-700">
                    Break Time (minutes)
                  </label>
                  <select
                    id="break_time"
                    name="break_time"
                    value={formData.break_time}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    <option value={0}>0 minutes</option>
                    <option value={30}>30 minutes</option>
                    <option value={45}>45 minutes</option>
                    <option value={60}>60 minutes</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="start_time" className="block text-sm font-medium text-gray-700">
                    Start Time
                  </label>
                  <input
                    type="time"
                    id="start_time"
                    name="start_time"
                    required
                    step="900"
                    value={formData.start_time}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>
                
                <div>
                  <label htmlFor="end_time" className="block text-sm font-medium text-gray-700">
                    End Time
                  </label>
                  <input
                    type="time"
                    id="end_time"
                    name="end_time"
                    required
                    step="900"
                    value={formData.end_time}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="remark" className="block text-sm font-medium text-gray-700">
                  Reason/Remark
                </label>
                <textarea
                  id="remark"
                  name="remark"
                  rows={3}
                  required
                  value={formData.remark}
                  onChange={handleInputChange}
                  placeholder="Please provide a reason for the overtime request..."
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {submitting ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  ) : null}
                  Submit Request
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Statistics */}
      {requests.length > 0 && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Overtime Statistics</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-600">
                  {calculateStatistics().approvedHours}h
                </div>
                <div className="text-sm text-gray-600">Total Approved</div>
                <div className="text-xs text-gray-500">
                  {calculateStatistics().approvedCount} request{calculateStatistics().approvedCount !== 1 ? 's' : ''}
                </div>
              </div>
              <div className="text-center p-4 bg-yellow-50 rounded-lg">
                <div className="text-2xl font-bold text-yellow-600">
                  {calculateStatistics().pendingHours}h
                </div>
                <div className="text-sm text-gray-600">Total Pending</div>
                <div className="text-xs text-gray-500">
                  {calculateStatistics().pendingCount} request{calculateStatistics().pendingCount !== 1 ? 's' : ''}
                </div>
              </div>
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">
                  {(parseFloat(calculateStatistics().approvedHours) + parseFloat(calculateStatistics().pendingHours)).toFixed(1)}h
                </div>
                <div className="text-sm text-gray-600">Total Hours</div>
                <div className="text-xs text-gray-500">All requests</div>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-2xl font-bold text-gray-600">
                  {requests.length}
                </div>
                <div className="text-sm text-gray-600">Total Requests</div>
                <div className="text-xs text-gray-500">In date range</div>
              </div>
            </div>
            <p className="mt-3 text-xs text-gray-500">
              * Duration calculations exclude break time
            </p>
          </div>
        </div>
      )}

      {/* Date Range Filter */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Filter by Date Range</h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label htmlFor="start_date" className="block text-sm font-medium text-gray-700">
                From Date
              </label>
              <input
                type="date"
                id="start_date"
                value={dateRange.start}
                onChange={(e) => setDateRange({...dateRange, start: e.target.value})}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
            <div>
              <label htmlFor="end_date" className="block text-sm font-medium text-gray-700">
                To Date
              </label>
              <input
                type="date"
                id="end_date"
                value={dateRange.end}
                onChange={(e) => setDateRange({...dateRange, end: e.target.value})}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={() => setDateRange(getDefaultDateRange())}
                className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Reset to Default
              </button>
            </div>
          </div>
          <p className="mt-2 text-sm text-gray-500">
            Default range: 21st of previous month to 20th of current month
          </p>
        </div>
      </div>

      {/* Requests List */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            My Overtime Requests ({requests.length})
          </h3>
          
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : requests.length === 0 ? (
            <div className="text-center py-12">
              <Clock className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No overtime requests</h3>
              <p className="mt-1 text-sm text-gray-500">
                Get started by creating your first overtime request.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Time Period
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Duration
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Reason
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Submitted
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Processed
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Manager Remark
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {requests.map((request) => (
                    <tr key={request.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {formatDate(request.date)}
                        <div className="text-xs text-gray-500">
                          {format(parseISO(request.date), 'EEEE')}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {format(parseISO(request.start_time), 'HH:mm')} - {format(parseISO(request.end_time), 'HH:mm')}
                        {request.is_overnight && (
                          <div className="text-xs text-orange-600">Overnight</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {calculateDuration(request.start_time, request.end_time, request.break_time).toFixed(1)}h
                        {request.break_time > 0 && (
                          <div className="text-xs text-gray-500">
                            Break: {request.break_time}min
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(request.status)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                        {request.remark}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {format(parseISO(request.date_created), 'MMM dd, HH:mm')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {request.processed_date
                          ? format(parseISO(request.processed_date), 'MMM dd, HH:mm')
                          : '--'
                        }
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate" title={request.manager_remark}>
                        {request.manager_remark || '--'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {request.status === 'pending' && (
                          <button
                            onClick={() => handleCancelRequest(request.id)}
                            className="inline-flex items-center px-2.5 py-1.5 border border-red-300 text-xs font-medium rounded text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                          >
                            <XCircle className="h-3 w-3 mr-1" />
                            Cancel
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Summary Stats
      {requests.length > 0 && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Summary</h3>
            
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {requests.length}
                </div>
                <div className="text-sm text-gray-500">Total Requests</div>
              </div>
              
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {requests.filter(r => r.status === 'approved').length}
                </div>
                <div className="text-sm text-gray-500">Approved</div>
              </div>
              
              <div className="text-center">
                <div className="text-2xl font-bold text-yellow-600">
                  {requests.filter(r => r.status === 'pending').length}
                </div>
                <div className="text-sm text-gray-500">Pending</div>
              </div>
              
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">
                  {requests
                    .filter(r => r.status === 'approved')
                    .reduce((sum, r) => sum + parseFloat(calculateDuration(r.start_time, r.end_time, r.break_time)), 0)
                    .toFixed(1)}h
                </div>
                <div className="text-sm text-gray-500">Approved Hours</div>
              </div>
            </div>
          </div>
        </div>
      )} */}
    </div>
  );
};

export default OvertimeManagement;
